
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const NAV_TIMEOUT_MS = 5000;

/**
 * Hook para botões de navegação com timeout automático.
 * Se a navegação não ocorrer em 5s, o botão é liberado novamente.
 */
export function useNavButton() {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const navigateTo = useCallback(
    (path: string, key?: string) => {
      const btnKey = key ?? path;

      // Já está carregando este botão — ignorar clique duplo
      if (loadingKey === btnKey) return;

      setLoadingKey(btnKey);
      clearTimer();

      // Timeout de segurança: libera o botão após 5s caso a página não carregue
      timerRef.current = setTimeout(() => {
        setLoadingKey(null);
      }, NAV_TIMEOUT_MS);

      navigate(path);
    },
    [loadingKey, navigate, clearTimer]
  );

  const isLoading = useCallback(
    (key: string) => loadingKey === key,
    [loadingKey]
  );

  const reset = useCallback(() => {
    clearTimer();
    setLoadingKey(null);
  }, [clearTimer]);

  return { navigateTo, isLoading, reset };
}
